import { controlScale } from './foundations';

const controlBase = {
  bg: 'surface.base',
  border: '1px solid',
  borderColor: 'line.input',
  borderRadius: 'lg',
  color: 'content.strong',
  fontSize: '13px',
  boxShadow: 'control',
  _hover: { borderColor: '#DCDCDC' },
  _focusVisible: { borderColor: 'line.ring', boxShadow: 'outline' },
  _invalid: {
    borderColor: 'rgba(214, 61, 61, 0.36)',
    boxShadow: 'control',
    _hover: { borderColor: 'rgba(214, 61, 61, 0.36)' },
    _focusVisible: {
      borderColor: 'rgba(214, 61, 61, 0.64)',
      boxShadow: '0 0 0 3px rgba(214, 61, 61, 0.16)',
    },
  },
  _disabled: {
    bg: 'surface.canvas',
    color: 'content.body',
    borderColor: 'line.input',
    boxShadow: 'none',
    opacity: 1,
    cursor: 'not-allowed',
    _placeholder: { color: 'content.faint' },
  },
};

const Button = {
  baseStyle: {
    fontFamily: 'body',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '20px',
    letterSpacing: '-0.15px',
    borderRadius: 'full',
    transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    _focusVisible: { boxShadow: 'outline' },
  },
  sizes: {
    xs: {
      h: controlScale.xs.h,
      minW: controlScale.xs.h,
      px: controlScale.xs.buttonPx,
      fontSize: controlScale.xs.font,
    },
    sm: {
      h: controlScale.sm.h,
      minW: controlScale.sm.h,
      px: controlScale.sm.buttonPx,
      fontSize: controlScale.sm.font,
    },
    md: {
      h: controlScale.md.h,
      minW: controlScale.md.h,
      px: controlScale.md.buttonPx,
      fontSize: controlScale.md.font,
    },
    lg: { h: '42px', minW: '42px', px: '19px', fontSize: '14px' },
  },
  variants: {
    solid: {
      bg: 'button.primary.500',
      color: 'content.inverse',
      border: '1px solid',
      borderColor: 'button.primary.500',
      boxShadow: 'none',
      _hover: { bg: 'button.primary.600', borderColor: 'button.primary.600' },
      _active: { bg: 'button.primary.700', borderColor: 'button.primary.700' },
    },
    outline: {
      bg: 'surface.base',
      color: 'content.strong',
      border: '1px solid',
      borderColor: 'line.input',
      boxShadow: 'control',
      _hover: { bg: 'surface.selected' },
      _active: { bg: 'surface.selected', boxShadow: 'none' },
    },
    destructive: {
      bg: 'state.destructive',
      color: 'content.inverse',
      border: '1px solid',
      borderColor: 'state.destructive',
      _hover: { bg: '#B32F2F', borderColor: '#B32F2F' },
      _active: { bg: '#8A2424', borderColor: '#8A2424' },
    },
    secondary: {
      bg: 'surface.canvas',
      color: 'content.strong',
      border: '1px solid',
      borderColor: 'line.hairline',
      _hover: { bg: 'surface.selected' },
      _active: { bg: 'surface.selected' },
      _expanded: { bg: 'surface.selected' },
    },
    ghost: {
      color: 'content.body',
      _hover: { bg: 'surface.selected', color: 'content.strong' },
      _active: { bg: 'surface.selected' },
    },
    link: {
      color: 'state.link',
      fontWeight: 500,
      _hover: { textDecoration: 'underline' },
    },
  },
  defaultProps: { variant: 'solid', size: 'md' },
};

const IconButton = {
  baseStyle: { borderRadius: controlScale.md.radius },
};

const Input = {
  baseStyle: {
    field: { fontFamily: 'body' },
  },
  sizes: {
    xs: {
      field: {
        h: controlScale.xs.h,
        fontSize: controlScale.xs.font,
        px: controlScale.xs.px,
        borderRadius: controlScale.xs.radius,
      },
    },
    sm: {
      field: {
        h: controlScale.sm.h,
        fontSize: controlScale.sm.font,
        px: controlScale.sm.px,
        borderRadius: controlScale.sm.radius,
      },
    },
    md: {
      field: {
        h: controlScale.md.h,
        fontSize: controlScale.md.font,
        px: controlScale.md.px,
        borderRadius: controlScale.md.radius,
      },
    },
    lg: {
      field: {
        h: controlScale.md.h,
        fontSize: controlScale.md.font,
        px: controlScale.md.px,
        borderRadius: controlScale.md.radius,
      },
    },
  },
  variants: {
    outline: { field: controlBase },
    filled: {
      field: {
        ...controlBase,
        bg: 'surface.selected',
        borderColor: 'transparent',
        _hover: { bg: 'surface.selected' },
      },
    },
  },
  defaultProps: { variant: 'outline', size: 'md' },
};

const Textarea = {
  baseStyle: { fontFamily: 'body' },
  sizes: {
    sm: { fontSize: controlScale.sm.font, px: controlScale.sm.px, py: '8px' },
    md: { fontSize: controlScale.md.font, px: controlScale.md.px, py: '9px' },
  },
  variants: { outline: { ...controlBase, minH: '96px' } },
  defaultProps: { variant: 'outline', size: 'md' },
};

const Checkbox = {
  baseStyle: {
    control: {
      borderRadius: '4px',
      borderColor: 'line.input',
      _checked: {
        bg: 'button.primary.500',
        borderColor: 'button.primary.500',
        _hover: { bg: 'button.primary.600', borderColor: 'button.primary.600' },
      },
    },
    label: { fontSize: controlScale.md.font, color: 'content.body' },
  },
  sizes: {
    md: { control: { w: '16px', h: '16px' }, label: { fontSize: controlScale.md.font } },
  },
  defaultProps: { size: 'md' },
};

const Radio = {
  baseStyle: {
    control: {
      borderColor: 'line.input',
      _checked: {
        bg: 'button.primary.500',
        borderColor: 'button.primary.500',
        _hover: { bg: 'button.primary.600', borderColor: 'button.primary.600' },
      },
    },
    label: { fontSize: controlScale.md.font, color: 'content.body' },
  },
  sizes: { md: { control: { w: '16px', h: '16px' } } },
  defaultProps: { size: 'md' },
};

const PinInput = {
  sizes: {
    md: {
      w: controlScale.md.h,
      h: controlScale.md.h,
      fontSize: controlScale.md.font,
      borderRadius: controlScale.md.radius,
    },
  },
  variants: { outline: controlBase },
  defaultProps: { variant: 'outline', size: 'md' },
};

const Slider = {
  baseStyle: {
    track: { bg: 'surface.selected', h: '4px', borderRadius: 'full' },
    filledTrack: { bg: 'button.primary.500' },
    thumb: { w: '14px', h: '14px', border: '1px solid', borderColor: 'line.input' },
  },
};

const Select = {
  sizes: {
    sm: {
      field: {
        h: controlScale.sm.h,
        fontSize: controlScale.sm.font,
        px: controlScale.sm.px,
        borderRadius: controlScale.sm.radius,
      },
    },
    md: {
      field: {
        h: controlScale.md.h,
        fontSize: controlScale.md.font,
        px: controlScale.md.px,
        borderRadius: controlScale.md.radius,
      },
    },
  },
  variants: {
    outline: { field: { ...controlBase, cursor: 'pointer' } },
  },
  defaultProps: { variant: 'outline', size: 'md' },
};

const NumberInput = {
  sizes: {
    sm: {
      field: {
        h: controlScale.sm.h,
        fontSize: controlScale.sm.font,
        borderRadius: controlScale.sm.radius,
      },
    },
    md: {
      field: {
        h: controlScale.md.h,
        fontSize: controlScale.md.font,
        borderRadius: controlScale.md.radius,
      },
    },
  },
  variants: { outline: { field: controlBase } },
  defaultProps: { variant: 'outline', size: 'md' },
};

const Table = {
  baseStyle: {
    table: { fontVariantNumeric: 'normal' },
  },
  variants: {
    serra: {
      table: { borderCollapse: 'separate', borderSpacing: 0 },
      thead: {
        tr: { bg: 'surface.frame' },
      },
      th: {
        fontFamily: 'body',
        fontSize: '12px',
        lineHeight: '12px',
        fontWeight: 400,
        letterSpacing: '-0.15px',
        textTransform: 'none',
        color: 'content.subtle',
        bg: 'transparent',
        h: '40px',
        px: '12px',
        py: 0,
        borderBottom: '1px solid',
        borderColor: 'line.hairline',
      },
      td: {
        fontSize: '13px',
        lineHeight: '20px',
        color: 'content.strong',
        h: '45px',
        px: '12px',
        py: '8px',
        borderBottom: '1px solid',
        borderColor: 'line.hairline',
      },
      tbody: {
        tr: {
          transition: 'background 0.12s ease',
          _hover: { bg: 'surface.selected' },
          '&:last-of-type td': { borderBottom: 'none' },
        },
      },
      tfoot: {
        td: { borderBottom: 'none', bg: 'surface.base' },
      },
    },
  },
  defaultProps: { variant: 'serra', size: 'md' },
};

const Badge = {
  baseStyle: {
    fontFamily: 'body',
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 500,
    letterSpacing: '-0.15px',
    textTransform: 'none',
    borderRadius: 'sm',
    px: '8px',
    py: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    h: '20px',
  },
  variants: {
    neutral: { bg: 'surface.selected', color: 'content.body' },
    info: { bg: 'state.linkSurface', color: 'state.link' },
    success: { bg: 'state.successSurface', color: 'state.success' },
    warning: { bg: 'state.warningSurface', color: 'state.warning' },
    destructive: { bg: 'state.destructiveSurface', color: 'state.destructive' },
    solid: { bg: 'surface.selected', color: 'content.body' },
    subtle: { bg: 'surface.selected', color: 'content.body' },
  },
  defaultProps: { variant: 'neutral' },
};

const Tag = {
  baseStyle: {
    container: {
      fontFamily: 'body',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: '-0.15px',
      borderRadius: 'sm',
      bg: 'surface.selected',
      color: 'content.body',
      minH: '20px',
      px: '6px',
      gap: '4px',
    },
    label: { lineHeight: '16px' },
    closeButton: {
      w: '14px',
      h: '14px',
      minW: '14px',
      fontSize: '12px',
      ml: 0,
      mr: '-2px',
      opacity: 1,
      color: 'content.faint',
      borderRadius: '4px',
      _hover: { color: 'content.strong', bg: 'transparent', opacity: 1 },
    },
  },
  sizes: {
    sm: { container: { minH: '20px', px: '6px', fontSize: '12px' } },
    md: { container: { minH: '20px', px: '6px', fontSize: '12px' } },
    lg: { container: { minH: '26px', px: '8px', fontSize: '13px' } },
  },
  variants: {
    subtle: { container: { bg: 'surface.selected', color: 'content.body' } },
    solid: { container: { bg: 'surface.selected', color: 'content.body' } },
    outline: {
      container: {
        bg: 'surface.base',
        color: 'content.body',
        boxShadow: 'inset 0 0 0 1px var(--chakra-colors-line-input)',
      },
    },
  },
  defaultProps: { variant: 'subtle', size: 'md' },
};

const Heading = {
  baseStyle: {
    fontFamily: 'display',
    fontWeight: 500,
    letterSpacing: '-0.15px',
    color: 'content.strong',
    m: 0,
  },
  sizes: {
    xs: { fontSize: '13px', lineHeight: '20px', fontFamily: 'heading' },
    sm: { fontSize: '14px', lineHeight: '20px', fontFamily: 'heading' },
    md: { fontSize: '16px', lineHeight: '26px', fontFamily: 'heading' },
    lg: { fontSize: '18px', lineHeight: '26px' },
    xl: { fontSize: '24px', lineHeight: '30px' },
    '2xl': { fontSize: '24px', lineHeight: '30px' },
  },
  defaultProps: { size: 'lg' },
};

const Text = {
  baseStyle: { fontFamily: 'body', letterSpacing: '-0.15px' },
};

const FormLabel = {
  baseStyle: {
    fontSize: '13px',
    lineHeight: '20px',
    fontWeight: 500,
    color: 'content.strong',
    mb: '8px',
    mr: 0,
    _disabled: { opacity: 0.64 },
  },
};

const FormError = {
  baseStyle: {
    text: { fontSize: '12px', lineHeight: '16px', color: 'state.destructive', mt: '8px' },
  },
};

const Form = {
  baseStyle: {
    requiredIndicator: { color: 'content.faint', ml: '2px' },
    helperText: { fontSize: '12px', lineHeight: '16px', color: 'content.body', mt: '8px' },
  },
};

const Menu = {
  baseStyle: {
    list: {
      bg: 'surface.base',
      border: '1px solid',
      borderColor: 'line.hairline',
      borderRadius: 'lg',
      boxShadow: 'popover',
      py: '4px',
    },
    item: {
      fontSize: '13px',
      color: 'content.strong',
      borderRadius: 'sm',
      mx: '4px',
      px: '8px',
      h: '32px',
      w: 'auto',
      bg: 'transparent',
      _hover: { bg: 'surface.selected' },
      _focus: { bg: 'transparent' },
      '&[data-focus]': { bg: 'transparent' },
      '&[data-focus]:hover': { bg: 'surface.selected' },
      _focusVisible: { bg: 'surface.selected' },
      _active: { bg: 'surface.selected' },
    },
  },
};

const Popover = {
  baseStyle: {
    content: {
      bg: 'surface.base',
      border: '1px solid',
      borderColor: 'line.hairline',
      borderRadius: 'lg',
      boxShadow: 'popover',
      _focus: { boxShadow: 'popover' },
      _focusVisible: { boxShadow: 'popover', outline: 'none' },
    },
    body: { p: 0 },
  },
};

const Tooltip = {
  baseStyle: {
    bg: 'content.strong',
    color: 'content.inverse',
    fontSize: '12px',
    borderRadius: 'sm',
    px: '8px',
    py: '4px',
  },
};

const Modal = {
  baseStyle: {
    overlay: { bg: 'rgba(0, 0, 0, 0.32)', backdropFilter: 'blur(4px)' },
    dialog: {
      bg: 'surface.base',
      borderRadius: 'xl',
      border: '1px solid',
      borderColor: 'line.hairline',
      boxShadow: 'popover',
      overflow: 'hidden',
      my: '16px',
    },
    header: {
      fontFamily: 'display',
      fontSize: '18px',
      lineHeight: '26px',
      fontWeight: 500,
      color: 'content.strong',
      px: '24px',
      pt: '24px',
      pb: '16px',
    },
    closeButton: {
      top: '18px',
      insetEnd: '18px',
      w: '28px',
      h: '28px',
      borderRadius: 'lg',
      color: 'content.subtle',
      _hover: { bg: 'surface.selected', color: 'content.strong' },
    },
    body: { fontSize: '13px', color: 'content.body', px: '24px', pb: '24px' },
    footer: {
      gap: '8px',
      px: '24px',
      py: '16px',
      bg: 'surface.frame',
      borderTop: '1px solid',
      borderColor: 'line.hairline',
    },
  },
};

const Card = {
  baseStyle: {
    container: {
      bg: 'surface.base',
      borderRadius: 'xl',
      border: '1px solid',
      borderColor: 'line.hairline',
      boxShadow: 'card',
    },
  },
};

const Divider = {
  baseStyle: { borderColor: 'line.hairline', opacity: 1 },
};

const Switch = {
  baseStyle: {
    track: {
      '--switch-bg': 'colors.line.input',
      p: '2px',
      transition: 'background 0.2s ease, box-shadow 0.2s ease',
      _checked: { '--switch-bg': 'colors.state.toggle' },
      _focusVisible: { boxShadow: 'outline' },
      _disabled: { opacity: 0.64, cursor: 'not-allowed' },
    },
    thumb: { bg: 'white', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' },
  },
  sizes: {
    sm: {
      container: { '--switch-track-width': '24px', '--switch-track-height': '12px' },
    },
    md: {
      container: { '--switch-track-width': '32px', '--switch-track-height': '16px' },
    },
    lg: {
      container: { '--switch-track-width': '40px', '--switch-track-height': '20px' },
    },
  },
  defaultProps: { size: 'md' },
};

const Progress = {
  baseStyle: {
    track: { bg: 'surface.selected', borderRadius: 'full' },
    filledTrack: { bg: 'state.link' },
  },
};

const Alert = {
  baseStyle: {
    container: { borderRadius: 'lg', fontSize: '13px' },
  },
};

const tabBase = {
  tab: {
    h: '36px',
    px: '14px',
    fontSize: '13px',
    fontWeight: 400,
    color: 'content.body',
    borderRadius: 0,
    border: 'none',
    borderBottom: '2px solid transparent',
    _selected: {
      color: 'content.strong',
      fontWeight: 500,
      bg: 'surface.base',
      borderBottomColor: 'content.strong',
    },
    _hover: { bg: 'rgba(41, 41, 41, 0.02)' },
  },
  tablist: {
    bg: 'surface.frame',
    borderBottom: '1px solid',
    borderColor: 'line.hairline',
  },
  tabpanel: { px: 0, py: '16px' },
};

const Tabs = {
  variants: {
    line: tabBase,
    enclosed: tabBase,
    'soft-rounded': tabBase,
  },
  defaultProps: { variant: 'line' },
};

const Avatar = {
  baseStyle: {
    container: { bg: 'surface.selected', color: 'content.subtle', fontWeight: 500 },
  },
};

export const components = {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Menu,
  Modal,
  NumberInput,
  PinInput,
  Popover,
  Progress,
  Radio,
  Select,
  Slider,
  Switch,
  Form,
  FormError,
  Table,
  Tabs,
  Tag,
  Text,
  Textarea,
  Tooltip,
};
